import { useEffect, useState } from 'react'
import { geocodeByAddress } from 'react-places-autocomplete'

import { ICoordinates } from 'modules/HomesForSale/types'

const getCityNameFromGeocodedAddress = (
  addressComponents: google.maps.GeocoderAddressComponent[],
): string => {
  const city
    = addressComponents.find(component => component.types.includes('locality'))
    || addressComponents.find(component => component.types.includes('administrative_area_level_1'))
    || addressComponents.find(component => component.types.includes('administrative_area_level_2'))
    || addressComponents.find(component => component.types.includes('administrative_area_level_3'))
    || addressComponents.find(component => component.types.includes('country'))

  return city ? city.long_name : ''
}

type IUseGeocodedPlace = (
  location: ICoordinates | null,
  foundSearchLocationGeocodedPlace: google.maps.GeocoderResult | null,
) => {
  geocodedPlace: google.maps.GeocoderResult | null
  formattedPlaceName: string
  onGeocodedPlaceChange: (place: google.maps.GeocoderResult) => void
}

/**
 * Geocoded Place: geocoded google location object w/ lots of data about the location
 *
 * This is used to:
 * - show the city name in the listing grid results
 * - show a city/state boundary on the map (coming in v2)
 * */
const useGeocodedPlace: IUseGeocodedPlace = (location, foundSearchLocationGeocodedPlace) => {
  const [geocodedPlace, setGeocodedPlace] = useState<google.maps.GeocoderResult | null>(null)
  const onGeocodedPlaceChange = (newPlace: google.maps.GeocoderResult) => setGeocodedPlace(newPlace)

  const [formattedPlaceName, setFormattedPlaceName] = useState('')

  useEffect(() => {
    if (!location) return

    const getPlaceByCoordinates = async () => {
      try {
        const places = await geocodeByAddress(`${location.latitude}, ${location.longitude}`)

        // For v1, let's keep this around so we can debug strange results if we need to
        console.warn({ places })

        if (places.length) setGeocodedPlace(places[0])
      } catch (error) {
        console.warn({ error })
      }
    }

    getPlaceByCoordinates()
  }, [location?.latitude, location?.longitude])

  // find the city name from the geocoded place
  useEffect(() => {
    if (!geocodedPlace || !geocodedPlace.address_components?.length) return

    const name = getCityNameFromGeocodedAddress(geocodedPlace.address_components)
    if (name) setFormattedPlaceName(name)
  }, [geocodedPlace])

  useEffect(() => {
    if (!foundSearchLocationGeocodedPlace) return

    setGeocodedPlace(foundSearchLocationGeocodedPlace)
  }, [foundSearchLocationGeocodedPlace])

  return {
    geocodedPlace,
    formattedPlaceName,
    onGeocodedPlaceChange,
  }
}

export default useGeocodedPlace
